import { Badge, Button, EmptyState, TextField } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Bus, ExternalLink } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { apiRequest } from "../../common/api/apiClient.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import type { components } from "../../generated/api.js";
import { travelTime } from "./travelTime.js";

type TripDTO = components["schemas"]["TripDTO"];

function today(): string {
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/**
 * Verbindungssuche: Start, Ziel, Reisetag. Die Suche steht in der Adresse, damit
 * Zurück aus einer Buchung wieder bei den Treffern landet und sich ein Link teilen lässt.
 */
export function TripSearchPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const query = { from: params.get("von") ?? "", to: params.get("nach") ?? "", date: params.get("tag") ?? today() };
  const [from, setFrom] = useState(query.from);
  const [to, setTo] = useState(query.to);
  const [date, setDate] = useState(query.date);
  const ready = query.from.trim().length >= 2 && query.to.trim().length >= 2;

  const trips = useQuery({
    queryKey: ["trips", query.from, query.to, query.date],
    enabled: ready,
    queryFn: () =>
      apiRequest<TripDTO[]>(
        `/trips?from=${encodeURIComponent(query.from)}&to=${encodeURIComponent(query.to)}&date=${query.date}`,
        { tenant: null },
      ),
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-6">
      <h1 className="pt-5 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Fahrten</h1>
      <form
        className="card mt-4 grid gap-3 p-4 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          setParams({ von: from, nach: to, tag: date });
        }}
      >
        <TextField label="Von" required minLength={2} value={from} onChange={(event) => setFrom(event.target.value)} />
        <TextField label="Nach" required minLength={2} value={to} onChange={(event) => setTo(event.target.value)} />
        <label className="grid gap-1 text-[12px] font-bold text-ink-2">
          Reisetag
          <input
            type="date"
            className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink"
            value={date}
            min={today()}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <Button type="submit">Suchen</Button>
      </form>

      {trips.isFetching && <p className="mt-4 text-[14px] text-ink-3">Wird gesucht …</p>}
      {trips.isError && <p className="mt-4 text-[14px] text-danger">Die Suche hat nicht geklappt.</p>}
      {trips.data?.length === 0 && (
        <EmptyState
          icon={<Bus aria-hidden="true" />}
          title="Keine Fahrt gefunden"
          description="An diesem Tag fährt nichts von hier nach dort. Ein anderer Tag oder ein kürzerer Ortsname hilft oft."
        />
      )}

      <ul className="mt-4 flex flex-col gap-2.5">
        {trips.data?.map((trip) => (
          <li key={`${trip.source}-${trip.serviceId ?? trip.externalUrl}-${trip.departsAt}`}>
            <TripCard trip={trip} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Ein Treffer. Eigene Fahrten führen zur Buchung, fremde zum Anbieter. */
function TripCard({ trip }: { trip: TripDTO }): JSX.Element {
  const body = (
    <>
      <div className="flex items-center gap-2 text-[15px] font-bold text-ink">
        <span className="num">{formatWhen(trip.departsAt, trip.fromTimeZone)}</span>
        <ArrowRight size={14} aria-hidden="true" />
        <span className="num">{formatWhen(trip.arrivesAt, trip.toTimeZone)}</span>
      </div>
      <p className="mt-0.5 text-[13px] text-ink-2">
        {trip.fromName} → {trip.toName} · <span className="num">{travelTime(trip.departsAt, trip.arrivesAt)}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge>{trip.sourceLabel}</Badge>
        {trip.free !== null && (
          <span className="num text-[12.5px] font-bold text-ink-3">{trip.free === 0 ? "ausgebucht" : `${trip.free} frei`}</span>
        )}
        <span className="flex-1" />
        {trip.amountCents !== null && (
          <span className="text-[15px] font-extrabold text-ink">
            <Money cents={trip.amountCents} currency={trip.currency} />
          </span>
        )}
      </div>
    </>
  );
  if (trip.serviceId) {
    return (
      <Link
        to={`/angebot/${encodeURIComponent(trip.serviceId)}?von=${trip.fromStop}&nach=${trip.toStop}`}
        className="card hover pad block"
      >
        {body}
      </Link>
    );
  }
  return (
    <a href={trip.externalUrl ?? "#"} target="_blank" rel="noreferrer" className="card hover pad block">
      {body}
      <span className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-bold text-ink-2">
        Beim Anbieter buchen <ExternalLink size={12} aria-hidden="true" />
      </span>
    </a>
  );
}
